import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type RefObject, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { Icon } from './Icon';
import { spaceForDrawing } from '../state/spacePan';
import { placeMenu, type Box, type MenuAnchor } from './menuPlacement';
import { shortcutAria, shortcutLabel, type HotkeyAction } from './hotkeys';

/**
 * A row of a menu. `checked` marks the current one of a set (a menuitemradio); `content` draws the row instead of the
 * plain label (an inline rename field); `trailing` puts action buttons at its end, shown on hover and reachable with
 * ArrowRight; `handle` gives it a drag handle, and the menu's `onReorder` learns where it was dropped (Alt+↑/↓ too).
 * `shortcut` prints the key of an editor command from `HOTKEYS`; `submenu` opens its rows beside it on hover or ArrowRight;
 * `badge` is a pill right after the label (a plan's name), coloured by its `tone`.
 */
type Entry = { id: string; label: string; icon?: ReactNode; disabled?: boolean; danger?: boolean; onSelect?: () => void; href?: string; target?: string; download?: string; keepOpen?: boolean;
  checked?: boolean; content?: ReactNode; trailing?: ReactNode; handle?: boolean; className?: string; shortcut?: HotkeyAction; badge?: { label: string; tone?: string }; submenu?: SubmenuItem[] };
export type MenuRow = Pick<Entry, 'id' | 'label' | 'icon' | 'disabled' | 'danger' | 'onSelect' | 'shortcut' | 'href' | 'badge'>;
type Separator = { id: string; separator: true };
export type SubmenuItem = MenuRow | Separator;
export type MenuItem = Entry | Separator;
type DropdownProps = {
  label: string; trigger: ReactNode; items: MenuItem[]; value?: string; selection?: boolean;
  disabled?: boolean; className?: string; triggerClassName?: string; heading?: ReactNode;
  onOpenChange?: (open: boolean) => void; contain?: string; width?: number; align?: 'start' | 'end';
  /** Opens or closes the menu from elsewhere (another control); `onOpenChange` reports every change back. Unset: the button alone. */
  open?: boolean;
  /** That other control: a press on it or the focus moving to it is not outside the menu, so it can close the menu itself. */
  partner?: RefObject<HTMLElement | null>;
  /** The rows with a handle can be dragged among themselves; `toIndex` counts those rows. */
  onReorder?: (id: string, toIndex: number) => void;
  /** Lays the rows out as a grid of this many columns (the chat's emoji picker): the arrows move by cell and by row. */
  columns?: number;
  /** A context menu: no button, open at this point while it is set; `onClose` hears it close. */
  at?: { x: number; y: number } | null; onClose?: () => void;
  /** The trigger's tooltip (`TooltipLayer`), above it for a button at the bottom, and the rows' drag handle's. */
  tip?: string; tipSide?: 'above'; handleTip?: string;
  /** The panel shows at most this many rows and scrolls the rest. */
  maxRows?: number;
};
const isEntry = (item: MenuItem): item is Entry => !('separator' in item);
const subRows = (rows: SubmenuItem[] = []) => rows.filter((row): row is MenuRow => !('separator' in row));
const OPENING_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End']);
const MENU_MIN_WIDTH = 220, MENU_MAX_WIDTH = 320;
/** The button's box without its own hover or press scale, so a menu opened mid-spring still lines up with it. */
const layoutRect = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect(), width = element.offsetWidth, height = element.offsetHeight;
  return width && height ? new DOMRect(rect.left + (rect.width - width) / 2, rect.top + (rect.height - height) / 2, width, height) : rect;
};
const shortcutCap = (shortcut?: HotkeyAction) => shortcut && <kbd className="dropdown-shortcut" aria-hidden="true">{shortcutLabel(shortcut)}</kbd>;
const badgePill = (badge?: Entry['badge']) => badge && <span className="dropdown-badge" data-tone={badge.tone}>{badge.label}</span>;

function Dropdown({ label, trigger, items, value, selection = false, disabled, className = '', triggerClassName = '', heading, onOpenChange, contain, width: preferredWidth, align, onReorder, columns, at, onClose, tip, tipSide, handleTip, open: requested, partner, maxRows }: DropdownProps) {
  const id = useId(), root = useRef<HTMLDivElement>(null), button = useRef<HTMLButtonElement>(null), panel = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'closed' | 'open' | 'closing'>('closed');
  const phaseRef = useRef(phase), [active, setActive] = useState('');
  const latest = useRef(onOpenChange); latest.current = onOpenChange;
  const closed = useRef(onClose); closed.current = onClose;
  const opener = useRef<HTMLElement | null>(null), external = useRef(false), partnerPress = useRef(false), subPanel = useRef<HTMLDivElement>(null), subFocus = useRef(false);
  const [sub, setSub] = useState<string | null>(null), [subActive, setSubActive] = useState('');
  const subRef = useRef(sub); subRef.current = sub;
  const leaving = useRef<{ timer: number; to: string } | null>(null);
  const cancelLeave = () => { if (leaving.current) window.clearTimeout(leaving.current.timer); leaving.current = null; };
  const search = useRef({ text: '', at: 0 });
  const [drag, setDrag] = useState<{ id: string; from: number; to: number; step: number } | null>(null);
  const dragRef = useRef<{ id: string; from: number; to: number; step: number; rows: { id: string; top: number; bottom: number }[] } | null>(null);
  const open = phase === 'open';
  const handles = items.filter((item): item is Entry => isEntry(item) && !!item.handle);
  const enabled = items.filter(isEntry).filter(item => !item.disabled);
  const activeIndex = items.findIndex(item => isEntry(item) && !item.disabled && item.id === active);
  const close = (restore = false) => {
    if (phaseRef.current !== 'open') return;
    phaseRef.current = 'closing';
    setPhase(matchMedia('(prefers-reduced-motion: reduce)').matches ? 'closed' : 'closing'); setSub(null); cancelLeave();
    if (restore) (at !== undefined || external.current ? opener.current : button.current)?.focus({ preventScroll: true });
    external.current = false;
    latest.current?.(false); closed.current?.();
  };
  const show = (last = false) => {
    if (disabled) return;
    window.dispatchEvent(new CustomEvent('hp:dropdown-open', { detail: id }));
    search.current = { text: '', at: 0 };
    setActive(enabled.find(item => item.id === value)?.id ?? enabled[last ? enabled.length - 1 : 0]?.id ?? '');
    phaseRef.current = 'open'; setPhase('open'); latest.current?.(true);
  };
  const activate = (item: Entry) => {
    if (phaseRef.current !== 'open' || item.disabled) return;
    // A tap focuses nothing on iPad Safari: the row is made the active one, or the next render closes its submenu.
    if (item.submenu) { setActive(item.id); openSub(item, true); return; }
    if (!item.keepOpen) close(true);
    item.onSelect?.();
  };
  const openSub = (item: Entry, focus: boolean) => {
    subFocus.current = focus;
    if (focus) cancelLeave();
    if (sub === item.id) { if (focus) subPanel.current?.querySelector<HTMLElement>('[data-active="true"]')?.focus({ preventScroll: true }); return; }
    setSub(item.id); setSubActive(subRows(item.submenu).find(row => !row.disabled)?.id ?? '');
  };
  const closeSub = () => { cancelLeave(); setSub(null); subFocus.current = false; panel.current?.querySelector<HTMLElement>('[data-active="true"]')?.focus({ preventScroll: true }); };
  const activateSub = (row: MenuRow) => { if (phaseRef.current !== 'open' || row.disabled) return; close(true); row.onSelect?.(); };
  const bounds = (): Box => {
    const box = contain ? button.current?.closest(contain)?.getBoundingClientRect() : null;
    return { left: Math.max(8, (box?.left ?? 0) + 8), top: 8, right: Math.min(innerWidth - 8, (box?.right ?? innerWidth) - 8), bottom: innerHeight - 8 };
  };
  /** A given `width` is the menu's; otherwise it hugs its rows between 220 and 320 px, and a list is never narrower than its button. */
  const fitWidth = (element: HTMLElement, room: Box, fixed?: number, triggerWidth = 0) => {
    const max = room.right - room.left;
    if (fixed) { element.style.width = `${Math.min(Math.max(triggerWidth, fixed), max)}px`; return; }
    element.style.width = 'max-content';
    element.style.minWidth = `${Math.min(max, Math.max(MENU_MIN_WIDTH, selection ? triggerWidth : 0))}px`;
    element.style.maxWidth = `${Math.min(MENU_MAX_WIDTH, max)}px`;
  };
  /** Beside its row, from layout values: client rects read short while the entrance scales the card. */
  const placeSub = () => {
    const element = subPanel.current, outer = panel.current, id = subRef.current;
    const row = id ? outer?.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"]`) : null;
    if (!element || !outer || !row) return;
    const room = bounds(), top = outer.offsetTop + row.offsetTop - outer.scrollTop;
    fitWidth(element, room);
    const placed = placeMenu({ kind: 'side', rect: { left: outer.offsetLeft, right: outer.offsetLeft + outer.offsetWidth, top, bottom: top + row.offsetHeight } },
      { width: element.offsetWidth, height: element.offsetHeight }, room, 4);
    element.style.left = `${placed.left}px`; element.style.top = `${placed.top}px`; element.style.transformOrigin = placed.origin;
  };
  useEffect(() => {
    if (at === undefined) return;
    if (!at) { close(); return; }
    if (phaseRef.current !== 'open') { opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; show(); }
  }, [at]);
  useEffect(() => {
    if (requested && phaseRef.current !== 'open') {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null; external.current = true; show();
    } else if (requested === false) close();
  }, [requested]);
  const controlled = useRef(requested !== undefined); controlled.current = requested !== undefined;
  useEffect(() => () => { if (controlled.current && phaseRef.current === 'open') latest.current?.(false); }, []);
  useEffect(() => {
    if (phase !== 'closing') return;
    const timer = window.setTimeout(() => { phaseRef.current = 'closed'; setPhase('closed'); }, 140);
    return () => clearTimeout(timer);
  }, [phase]);
  useLayoutEffect(() => {
    if (!open || !panel.current) return;
    const element = panel.current;
    element.showPopover();
    const position = () => {
      const room = bounds();
      let anchor: MenuAnchor, rect: DOMRect | null = null;
      if (at) anchor = { kind: 'point', x: at.x, y: at.y };
      else {
        const trigger = button.current;
        if (!trigger?.isConnected || !trigger.getClientRects().length) { close(); return; }
        rect = layoutRect(trigger);
        for (let ancestor = trigger.parentElement; ancestor; ancestor = ancestor.parentElement) {
          if (!/(auto|scroll|hidden|clip)/.test(getComputedStyle(ancestor).overflowY)) continue;
          const clip = ancestor.getBoundingClientRect();
          if (rect.bottom <= clip.top || rect.top >= clip.bottom) { close(); return; }
        }
        anchor = { kind: 'button', rect, align: (align ? align === 'start' : selection) ? 'start' : 'end' };
      }
      fitWidth(element, room, preferredWidth, rect?.width);
      const rows = maxRows ? element.querySelectorAll<HTMLElement>('.dropdown-option') : null, last = rows && rows.length > maxRows! ? rows[maxRows! - 1] : null;
      const height = Math.min(selection ? 320 : innerHeight - 16, rect ? Math.max(innerHeight - rect.bottom - 14, rect.top - 14) : innerHeight - 16,
        last ? last.offsetTop + last.offsetHeight + parseFloat(getComputedStyle(element).paddingBottom) : Infinity);
      element.style.maxHeight = `${Math.max(44, height)}px`;
      const placed = placeMenu(anchor, { width: element.offsetWidth, height: Math.min(element.scrollHeight, height) }, room);
      element.style.left = `${placed.left}px`; element.style.top = `${placed.top}px`; element.style.transformOrigin = placed.origin;
      if (rect && (rect.bottom < 0 || rect.top > innerHeight)) close();
      placeSub();
    };
    position();
    const observer = new ResizeObserver(position); observer.observe(element); if (button.current) observer.observe(button.current);
    window.addEventListener('resize', position); window.addEventListener('scroll', position, true);
    return () => { observer.disconnect(); window.removeEventListener('resize', position); window.removeEventListener('scroll', position, true); };
  }, [open, items.length, heading, contain, selection, preferredWidth, align, at, maxRows]);
  useLayoutEffect(() => {
    const element = subPanel.current;
    if (!element || !sub) return;
    if (!element.matches(':popover-open')) element.showPopover();
    placeSub();
    const option = element.querySelector<HTMLElement>('[data-active="true"]');
    if (option && (subFocus.current || element.contains(document.activeElement))) option.focus({ preventScroll: true });
  }, [sub, subActive]);
  useLayoutEffect(() => {
    if (!open) return;
    const leavingSub = !!sub && sub !== active;
    if (leavingSub) setSub(null);
    if (!enabled.some(item => item.id === active)) { setActive(enabled[0]?.id ?? ''); return; }
    const option = panel.current?.querySelector<HTMLElement>('[data-active="true"]');
    if (option && panel.current) {
      // Layout offsets, not client rects: the entrance scales the card, and its boxes read 4 % short until it ends.
      const view = panel.current, top = option.offsetTop, bottom = top + option.offsetHeight;
      if (top < view.scrollTop + 6) view.scrollTop = top - 6;
      else if (bottom > view.scrollTop + view.clientHeight - 6) view.scrollTop = bottom - view.clientHeight + 6;
    }
    // The row takes the focus unless it holds it deeper (a rename field, its action buttons, its open submenu); a closing submenu hands it back.
    if (!selection && option && !option.contains(document.activeElement) && (leavingSub || !subPanel.current?.contains(document.activeElement))) option.focus({ preventScroll: true });
  }, [open, active, items]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      // WebKit does not focus a pressed button: the row blurs to nothing, and only the partner's click may close the menu.
      if (partner?.current?.contains(event.target as Node)) partnerPress.current = true;
      else if (!root.current?.contains(event.target as Node)) close();
    };
    const released = () => { partnerPress.current = false; };
    const another = (event: Event) => { if ((event as CustomEvent).detail !== id) close(); };
    window.addEventListener('pointerdown', outside, true); window.addEventListener('hp:dropdown-open', another);
    window.addEventListener('pointerup', released, true); window.addEventListener('pointercancel', released, true);
    return () => {
      window.removeEventListener('pointerdown', outside, true); window.removeEventListener('hp:dropdown-open', another);
      window.removeEventListener('pointerup', released, true); window.removeEventListener('pointercancel', released, true); partnerPress.current = false;
    };
  }, [open]);
  useEffect(() => { if (disabled) close(); }, [disabled]);
  useEffect(() => cancelLeave, []);
  /** The row's action buttons: ArrowRight walks into them, ArrowLeft walks back to the row. */
  const rowActions = (): HTMLElement[] => [...(panel.current?.querySelector('[data-active="true"]')?.querySelectorAll<HTMLElement>('.dropdown-actions button') ?? [])];
  const startDrag = (event: ReactPointerEvent<HTMLElement>, id: string) => {
    if (!onReorder || !panel.current || event.button > 0) return;
    event.preventDefault(); event.stopPropagation();
    const rows = [...panel.current.querySelectorAll<HTMLElement>('[data-handle="true"]')].map(row => { const rect = row.getBoundingClientRect(); return { id: row.dataset.id ?? '', top: rect.top, bottom: rect.bottom }; });
    const from = rows.findIndex(row => row.id === id);
    if (from < 0) return;
    const step = rows.length > 1 ? rows[1].top - rows[0].top : rows[0].bottom - rows[0].top;
    dragRef.current = { id, from, to: from, step, rows };
    event.currentTarget.setPointerCapture(event.pointerId);
    setActive(id);
    setDrag({ id, from, to: from, step });
  };
  const moveDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const state = dragRef.current;
    if (!state) return;
    const middle = event.clientY;
    let to = state.rows.filter((row, index) => index !== state.from && (row.top + row.bottom) / 2 < middle).length;
    to = Math.max(0, Math.min(state.rows.length - 1, to));
    if (to !== state.to) { state.to = to; setDrag({ id: state.id, from: state.from, to, step: state.step }); }
  };
  const endDrag = (event: ReactPointerEvent<HTMLElement>) => {
    const state = dragRef.current;
    if (!state) return;
    dragRef.current = null; setDrag(null);
    try { event.currentTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
    if (event.type !== 'pointercancel' && state.to !== state.from) onReorder?.(state.id, state.to);
  };
  /** Where a row stands while another is dragged past it. */
  const rowShift = (id: string): string | undefined => {
    if (!drag) return undefined;
    const index = handles.findIndex(item => item.id === id);
    if (index < 0) return undefined;
    if (id === drag.id) return `translateY(${(drag.to - drag.from) * drag.step}px)`;
    if (drag.to > drag.from && index > drag.from && index <= drag.to) return `translateY(${-drag.step}px)`;
    if (drag.to < drag.from && index >= drag.to && index < drag.from) return `translateY(${drag.step}px)`;
    return undefined;
  };
  const keyDown = (event: KeyboardEvent) => {
    // A closed menu's button keeps only the keys that open it, as the WAI-ARIA menu button does: a picked row hands the
    // focus back to it, and a letter, Escape, Delete or ⌘Z pressed there goes on to the editor. A list's button keeps
    // every key, as a native select does: its letters find an option. All but Escape, which a closed list has no use
    // for (the WAI-ARIA select-only combobox leaves it alone): it lets go of a followed view or closes a panel.
    if (!open && (selection ? event.key === 'Escape' : !OPENING_KEYS.has(event.key))) return;
    // Space pans the drawing instead when the pointer gave the button its focus and is over the drawing (spacePan.ts).
    if (!open && event.key === ' ' && spaceForDrawing()) return;
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const inActions = !!target.closest?.('.dropdown-actions');
    if (event.key === 'Tab') { close(panel.current?.contains(document.activeElement) || subPanel.current?.contains(document.activeElement)); return; }
    if (open && sub && subPanel.current?.contains(target)) {
      const rows = subRows((items.find(item => isEntry(item) && item.id === sub) as Entry | undefined)?.submenu).filter(row => !row.disabled);
      const index = rows.findIndex(row => row.id === subActive);
      if (event.key === 'Escape' || event.key === 'ArrowLeft') { event.preventDefault(); closeSub(); }
      else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        setSubActive(rows[event.key === 'Home' ? 0 : event.key === 'End' ? rows.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + rows.length) % rows.length]?.id ?? '');
      } else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); subPanel.current.querySelector<HTMLElement>('[data-active="true"]')?.click(); }
      return;
    }
    if (event.key === 'Escape') { if (open) { event.preventDefault(); if (sub) closeSub(); else close(true); } return; }
    const current = enabled.find(item => item.id === active);
    if (open && event.key === 'ArrowRight' && current?.submenu) { event.preventDefault(); openSub(current, true); return; }
    if (open && columns && ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      const index = enabled.findIndex(item => item.id === active);
      const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowDown' ? columns : -columns;
      const next = enabled[index + step];
      if (next) setActive(next.id);
      return;
    }
    if (open && (event.key === 'ArrowRight' || event.key === 'ArrowLeft')) {
      const actions = rowActions();
      if (!actions.length) return;
      event.preventDefault();
      const at = actions.indexOf(document.activeElement as HTMLElement);
      if (event.key === 'ArrowRight') actions[Math.min(actions.length - 1, at + 1)]?.focus({ preventScroll: true });
      else if (at > 0) actions[at - 1].focus({ preventScroll: true });
      else panel.current?.querySelector<HTMLElement>('[data-active="true"]')?.focus({ preventScroll: true });
      return;
    }
    if (open && onReorder && event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      const index = handles.findIndex(item => item.id === active);
      if (index < 0) return;
      event.preventDefault();
      const to = index + (event.key === 'ArrowDown' ? 1 : -1);
      if (to >= 0 && to < handles.length) onReorder(active, to);
      return;
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      if (!open) { show(event.key === 'ArrowUp' || event.key === 'End'); return; }
      const index = enabled.findIndex(item => item.id === active);
      setActive(enabled[event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length]?.id ?? '');
    } else if (event.key === 'Enter' || event.key === ' ') {
      if (inActions) return;
      event.preventDefault();
      if (!open) show(); else panel.current?.querySelector<HTMLElement>('[data-active="true"]')?.click();
    } else if (event.key.length === 1 && !event.altKey && !event.metaKey && !event.ctrlKey && !inActions) {
      event.preventDefault(); if (!open) show();
      const now = Date.now(), text = (now - search.current.at < 700 ? search.current.text : '') + event.key.toLocaleLowerCase();
      search.current = { text, at: now };
      const query = [...text].every(char => char === text[0]) ? text[0] : text;
      const index = enabled.findIndex(item => item.id === active);
      const ordered = [...enabled.slice(index + 1), ...enabled.slice(0, index + 1)];
      const match = ordered.find(item => item.label.toLocaleLowerCase().startsWith(query));
      if (match) setActive(match.id);
    }
  };
  return <div ref={root} className={`dropdown ${className}`} onKeyDown={keyDown} onPointerDown={event => event.stopPropagation()}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !partner?.current?.contains(event.relatedTarget) && !partnerPress.current) close(); }}>
    {at === undefined && <button ref={button} type="button" className={`dropdown-trigger ${triggerClassName}`} role={selection ? 'combobox' : undefined} data-tip={tip} data-tip-side={tipSide}
      aria-label={label} aria-haspopup={selection ? 'listbox' : 'menu'} aria-expanded={open} aria-controls={phase !== 'closed' ? id : undefined}
      aria-activedescendant={open && selection && activeIndex >= 0 ? `${id}-${activeIndex}` : undefined} disabled={disabled}
      onClick={() => open ? close() : show()}>{trigger}</button>}
    {phase !== 'closed' && <div ref={panel} id={id} popover="manual" role={selection ? 'listbox' : 'menu'} aria-label={label}
      className={'dropdown-panel' + (columns ? ' is-grid' : '')} style={columns ? { '--dropdown-columns': columns } as CSSProperties : undefined}
      data-dropdown-open={open} data-closing={!open} aria-hidden={!open} inert={!open}
      onClick={event => event.stopPropagation()}>
      {heading && <div className="dropdown-heading">{heading}</div>}
      {items.map((item, index) => !isEntry(item) ? <hr key={item.id} /> : (() => {
        const radio = !selection && item.checked !== undefined;
        const props = { id: `${id}-${index}`, role: selection ? 'option' : radio ? 'menuitemradio' : 'menuitem', tabIndex: -1,
          'aria-selected': selection ? item.id === value : undefined, 'aria-checked': radio ? item.checked : undefined, 'aria-disabled': item.disabled || undefined,
          'data-active': item.id === active, 'data-id': item.id, 'data-handle': item.handle || undefined, 'data-dragging': drag?.id === item.id || undefined,
          'aria-keyshortcuts': item.shortcut ? shortcutAria(item.shortcut) : undefined,
          'aria-haspopup': item.submenu ? 'menu' as const : undefined, 'aria-expanded': item.submenu ? sub === item.id : undefined,
          'aria-controls': item.submenu && sub === item.id ? `${id}-sub` : undefined,
          className: `dropdown-option${item.danger ? ' danger' : ''}${item.className ? ' ' + item.className : ''}`,
          style: rowShift(item.id) ? { transform: rowShift(item.id) } : undefined,
          onPointerMove: () => {
            if (item.disabled || dragRef.current) return;
            if (!sub || sub === item.id) { cancelLeave(); setActive(item.id); if (item.submenu) openSub(item, false); return; }
            // On the way to an open submenu the pointer crosses other rows, another submenu's too: they take over only if it stays on them.
            if (leaving.current) leaving.current.to = item.id;
            else leaving.current = { to: item.id, timer: window.setTimeout(() => {
              const to = leaving.current?.to; leaving.current = null;
              if (!to) return;
              setActive(to);
              const next = items.find(entry => isEntry(entry) && entry.id === to) as Entry | undefined;
              if (next?.submenu) openSub(next, false);
            }, 250) };
          }, onFocus: () => setActive(item.id),
          onClick: () => activate(item) };
        const content = <>
          {item.handle && <span className="dropdown-handle" aria-hidden="true" data-tip={handleTip} data-tip-side="above" onPointerDown={event => startDrag(event, item.id)} onPointerMove={moveDrag} onPointerUp={endDrag} onPointerCancel={endDrag}><Icon name="dragHandle" size={14} /></span>}
          {item.icon}<span className="dropdown-label">{item.content ?? item.label}</span>{badgePill(item.badge)}{shortcutCap(item.shortcut)}
          {item.submenu && <span className="dropdown-chevron" aria-hidden="true"><Icon name="chevronDown" size={16} /></span>}
          {(selection ? item.id === value : item.checked) && <span className="dropdown-check"><Icon name="check" size={16} /></span>}
          {item.trailing && <span className="dropdown-actions" onClick={event => event.stopPropagation()}>{item.trailing}</span>}
        </>;
        // A row with buttons of its own is named by its label alone, not by everything inside it.
        if (item.trailing || item.handle || item.content) return <div key={item.id} {...props} aria-label={item.label}>{content}</div>;
        return item.href && !item.disabled ? <a key={item.id} {...props} href={item.href} target={item.target} download={item.download} rel="noopener noreferrer">{content}</a>
          : <button key={item.id} type="button" {...props} disabled={item.disabled}>{content}</button>;
      })())}
    </div>}
    {open && sub && (() => {
      const parent = items.find(item => isEntry(item) && item.id === sub) as Entry | undefined;
      return parent?.submenu && <div ref={subPanel} id={`${id}-sub`} popover="manual" role="menu" aria-label={parent.label} className="dropdown-panel dropdown-submenu"
        onPointerEnter={cancelLeave} onClick={event => event.stopPropagation()}>
        {parent.submenu.map(row => {
          if ('separator' in row) return <hr key={row.id} />;
          const props = { role: 'menuitem', tabIndex: -1, 'aria-disabled': row.disabled || undefined, 'aria-keyshortcuts': row.shortcut ? shortcutAria(row.shortcut) : undefined,
            'data-active': row.id === subActive, className: `dropdown-option${row.danger ? ' danger' : ''}`,
            onPointerMove: () => { if (!row.disabled) setSubActive(row.id); }, onFocus: () => setSubActive(row.id), onClick: () => activateSub(row) };
          const content = <>{row.icon}<span className="dropdown-label">{row.label}</span>{badgePill(row.badge)}{shortcutCap(row.shortcut)}</>;
          return row.href && !row.disabled ? <a key={row.id} {...props} href={row.href} rel="noopener noreferrer">{content}</a>
            : <button key={row.id} type="button" {...props} disabled={row.disabled}>{content}</button>;
        })}
      </div>;
    })()}
  </div>;
}

export function SelectDropdown({ label, value, options, onChange, disabled, className, triggerClassName, leading }: {
  label: string; value: string; options: { value: string; label: string; disabled?: boolean }[]; onChange(value: string): void;
  disabled?: boolean; className?: string; triggerClassName?: string; leading?: ReactNode;
}) {
  return <Dropdown label={label} selection value={value} disabled={disabled} className={`dropdown-select ${className ?? ''}`} triggerClassName={triggerClassName}
    trigger={<>{leading}<span>{options.find(option => option.value === value)?.label ?? label}</span><Icon name="chevronDown" size={16} /></>}
    items={options.map(option => ({ id: option.value, label: option.label, disabled: option.disabled, onSelect: () => { if (option.value !== value) onChange(option.value); } }))} />;
}

export function ActionMenu(props: Omit<DropdownProps, 'value' | 'selection' | 'at' | 'onClose'>) {
  return <Dropdown {...props} />;
}

/** A menu at the cursor (a right-click): open while `at` is set; a pick or Escape hands the focus back to where it was. */
export function ContextMenu({ label, items, at, onClose, width, className }: { label: string; items: MenuItem[]; at: { x: number; y: number } | null; onClose(): void; width?: number; className?: string }) {
  return <Dropdown label={label} trigger={null} items={items} at={at} onClose={onClose} width={width} className={className} />;
}
